import { graphqlClient } from '@/lib/graphql-client';
import {
  buildOrderLiteral,
  buildPagingLiteral,
  buildWhereLiteral,
  OrderBy,
} from '@/lib/graphql-query-builder';
import { planDeliveredSlice } from './application-utils';
import { ApplicationBase, ListParams, PagingResult, RequestFilters } from './service-desk.types';

/** Newest application first, like the legacy `SortByDescending(x => x.AppliedDate)`
 * (NocApplicationService.cs:311, ByodApplicationService.cs:181, MetLifeApplicationService.cs:256). */
export const DEFAULT_APPLICATIONS_ORDER: OrderBy[] = [
  { field: 'AppliedDateUtc', direction: 'DESC' },
];

const isDefaultOrder = (order: OrderBy[]) =>
  JSON.stringify(order) === JSON.stringify(DEFAULT_APPLICATIONS_ORDER);

/** Whether a filter narrows `Status`, at the top level or inside the `$and` `mergeWhere` adds. */
const filtersOnStatus = (filter: Record<string, unknown>) =>
  'Status' in filter ||
  (Array.isArray(filter.$and) &&
    (filter.$and as Record<string, unknown>[]).some((clause) => 'Status' in clause));

/**
 * Builds the Data Gateway reads for one application schema.
 *
 * All five service desk schemas follow the same gateway naming (`getNocApplications`, ...), so the read
 * plumbing is generated from the schema name + field list instead of being copied five times. The Blocks
 * OS gateway has no bare `NocApplications` query field; the alias keeps the response key `plural`.
 * Writes never go through the gateway from the app: they are the service desk workflows
 * (application-workflows.ts), and the data rules let only super_admin write these schemas.
 *
 * `deliveredLast` (Business Card, ID Card) lists delivered applications after the open ones, like
 * the legacy `$sort: { _deliveredSort: 1, AppliedDate: -1 }` (BusinessCardApplicationService.cs:202,
 * IdCardApplicationService.cs:196), but only in the default order: a column the user sorts on
 * orders the whole list.
 */
export const createApplicationApi = <TItem extends ApplicationBase>(
  schemaName: string,
  fields: readonly string[],
  { deliveredLast = false }: { deliveredLast?: boolean } = {}
) => {
  const plural = `${schemaName}s`;
  const selection = ['ItemId', 'CreatedDate', ...fields].join('\n        ');

  /** The Data Gateway's `get<Schema>s` queries take native `where` / `order` / `paging` arguments
   * instead of a single JSON-string `input: DynamicQueryInput` — see graphql-query-builder.ts. */
  const buildListQuery = (
    where: Record<string, unknown>,
    pageNo: number,
    pageSize: number,
    order: OrderBy[]
  ) => `
    query {
      ${plural}: get${plural}(
        where: ${buildWhereLiteral(where)}
        order: ${buildOrderLiteral(order)}
        paging: ${buildPagingLiteral(pageNo, pageSize)}
      ) {
        hasNextPage
        hasPreviousPage
        totalCount
        totalPages
        pageSize
        pageNo
        items {
        ${selection}
        }
      }
    }
  `;

  const fetchPage = async (
    where: Record<string, unknown>,
    pageNo: number,
    pageSize: number,
    order: OrderBy[] = DEFAULT_APPLICATIONS_ORDER
  ): Promise<PagingResult<TItem>> => {
    const result = await graphqlClient.query<Record<string, PagingResult<TItem>>>({
      query: buildListQuery(where, pageNo, pageSize, order),
    });
    return result[plural];
  };

  /** Open applications first, then delivered ones (see `planDeliveredSlice`). */
  const listDeliveredLast = async (
    filter: Record<string, unknown>,
    pageNo: number,
    pageSize: number
  ): Promise<PagingResult<TItem>> => {
    const open = await fetchPage({ ...filter, Status: { $ne: 'Delivered' } }, pageNo, pageSize);
    const slice = planDeliveredSlice(open.totalCount, pageNo, pageSize);
    const deliveredFilter = { ...filter, Status: 'Delivered' };
    const deliveredPages = await Promise.all(
      (slice.pages.length ? slice.pages : [1]).map((page) =>
        fetchPage(deliveredFilter, page, slice.pages.length ? pageSize : 1)
      )
    );
    const delivered = slice.pages.length
      ? deliveredPages.flatMap((page) => page.items).slice(slice.start, slice.end)
      : [];
    const totalCount = open.totalCount + deliveredPages[0].totalCount;
    const totalPages = Math.ceil(totalCount / pageSize);
    return {
      hasNextPage: pageNo < totalPages,
      hasPreviousPage: pageNo > 1,
      totalCount,
      totalPages,
      pageSize,
      pageNo,
      items: [...open.items, ...delivered],
    };
  };

  const list = async (params: ListParams): Promise<PagingResult<TItem>> => {
    const { pageNo, pageSize, filter = {}, order = DEFAULT_APPLICATIONS_ORDER } = params;
    // A status filter decides which statuses show, so the plain order is already right.
    if (deliveredLast && isDefaultOrder(order) && !filtersOnStatus(filter)) {
      return listDeliveredLast(filter, pageNo, pageSize);
    }
    return fetchPage(filter, pageNo, pageSize, order);
  };

  const getById = async (itemId: string): Promise<TItem | null> => {
    const result = await fetchPage({ ItemId: itemId }, 1, 1);
    return result.items[0] ?? null;
  };

  return { schemaName, list, getById };
};

export type ApplicationApi<TItem extends ApplicationBase> = ReturnType<
  typeof createApplicationApi<TItem>
>;

/** Case-insensitive "contains" clause for a Mongo filter. The gateway turns `$regex` into a literal
 * `contains`, so the text is never regex-escaped. */
export const containsFilter = (text: string) => ({
  $regex: text.trim(),
  $options: 'i',
});

/**
 * The filter the admin "requests" lists send, mirroring the legacy `Build*Query` methods: optional
 * status equality, the department clause and a case-insensitive keyword match on the
 * applicant name. `departmentCondition` comes from `useRequestFilters` / `useDepartmentScope`.
 */
export const buildRequestsFilter = (
  filters: RequestFilters,
  keywordField: string,
  departmentCondition: string | { $in: string[] } | undefined,
  extra: Record<string, unknown> = {}
): Record<string, unknown> => ({
  ...(departmentCondition ? { DepartmentId: departmentCondition } : {}),
  ...(filters.status ? { Status: filters.status } : {}),
  ...(filters.keyword.trim() ? { [keywordField]: containsFilter(filters.keyword) } : {}),
  ...extra,
});
